import React, { useMemo, useState, useEffect } from "react";
import { Helmet } from "react-helmet";

// @ts-ignore
import InterFont from '@/assets/fonts/Inter.woff2';
import patientPageData from "@/router/patientPageData";
import hcpPageData from "@/router/hcpPageData";
import { useLocation } from "react-router-dom";

/**
 * @description PageHelmet component to manage page metadata and SEO tags.
 * It dynamically loads metadata based on the current page name and applies noindex for specific routes.
 * if _meta.js file exists in the page directory, it will be imported and merged with the page data.
 * otherwise, it will use the page data as is.
 * 
 * @component PageHelmet
 * 
 * @param {Object} props - The component props.
 * @param {string} props.pageName - The name of the current page.
 * @param {React.ReactNode} props.children - The child components to render within the Helmet 
 * @returns 
 */
function PageHelmet({pageName, children}) {
  const Location = useLocation();
  const pageData = useMemo(() => {
    const filterPatient = patientPageData.filter((page) => page.name === pageName);
    const filterHcp = hcpPageData.filter((page) => page.name === pageName);
    const filter = [...filterHcp, ...filterPatient];
    
    return filter.length ? filter[0] : undefined;
  }, [pageName]);

  const [pageHead, setPageHead] = useState(null);

  useEffect(() => {
    const loadMeta = async () => {
      if (pageData?.name) {
        try {
          const module = await import(`@/pages/${pageData.name}/_meta.js`);
          const metaData = module.default || module;

          setPageHead({...pageData, ...metaData});
        } catch {
          setPageHead(pageData || null);
        }
      } else {
        setPageHead(pageData || null);
      }
    };

    loadMeta();
  }, [pageData]);

  // apply noindex for the following routes
  const isNoIndex = useMemo(() => (
    Location.pathname.replace(/\/$/, "") === '/living-with-cah' ||
    Location.pathname.replace(/\/$/, "") === '/hcp/excess-androgens' ||
    Location.pathname.replace(/\/$/, "") === '/hcp/glucocorticoid-challenges' ||
    Location.pathname.replace(/\/$/, "") === '/hcp/treating-cah'
  ), [Location]);

  /* useEffect(() => {
    const inject = () => {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/manifest.json';
      link.crossOrigin = 'use-credentials';
      document.head.appendChild(link);
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(inject);
    } else {
      setTimeout(inject, 0);
    }
  }, []); */

  return (
    <>
      {/* @ts-ignore: Helmet typing is incompatible with current React types but is valid at runtime */}
      <Helmet>
        <title>{pageHead?.title}</title>
        <link rel="font" type="font/woff2" href={InterFont} />
        {isNoIndex && <meta name="robots" content="noindex" />}
        {pageHead?.metadata?.map((meta, i) => <meta key={`meta_${i}`} {...meta} />)}
        {pageHead?.link?.map((link, i) => <link key={`link_${i}`} {...link} />)}
        {pageHead?.schemaTag?.map((tag, i) => <script key={`tag_${i}`} type="application/ld+json">{JSON.stringify(tag)}</script>)}
        {/* <link crossOrigin="use-credentials" rel="manifest" href="/manifest.json" /> */}
      </Helmet>
      {children}
    </>
  )
}

export default PageHelmet;